/**
 * Browser API bases.
 *
 * Dev (npm start): defaults to http://127.0.0.1:<port> — unchanged for local launch_all.
 * Prod (Docker build): set REACT_APP_API_PATH_PROXY=1 and REACT_APP_API_ORIGIN
 *   ("" = same-origin relative /svc/... under HTTPS, or https://www.evolve-x.eu).
 * Nginx in the image proxies /svc/<name>/ → the matching backend (avoids mixed content).
 */

const PATH_PROXY =
    process.env.REACT_APP_API_PATH_PROXY === '1' ||
    process.env.REACT_APP_API_PATH_PROXY === 'true';

function runtimeDefaultApiOrigin() {
    if (typeof window !== 'undefined' && window.location) {
        const protocol = window.location.protocol || 'http:';
        const host = window.location.hostname || '127.0.0.1';
        return `${protocol}//${host}`;
    }
    return 'http://127.0.0.1';
}

const API_ORIGIN =
    process.env.REACT_APP_API_ORIGIN === undefined
        ? runtimeDefaultApiOrigin()
        : process.env.REACT_APP_API_ORIGIN;

function joinOrigin(base, path) {
    const p = path.startsWith('/') ? path : `/${path}`;
    if (!base) return p;
    return `${String(base).replace(/\/$/, '')}${p}`;
}

/** Dev: origin:port · Prod path-proxy: origin + /svc/... */
function serviceBase(devPort, svcPath) {
    if (PATH_PROXY) {
        return joinOrigin(API_ORIGIN, svcPath);
    }
    return `${API_ORIGIN}:${devPort}`;
}

export const API_ORIGIN_BASE = API_ORIGIN;
export const API_PATH_PROXY_ENABLED = PATH_PROXY;

/**
 * Dev auth/gateway use same-origin CRA proxies (src/setupProxy.js) so guest
 * login works when only port 3000 is forwarded to the browser.
 * Prod keeps /svc/auth and /svc/gateway via nginx.
 */
export const API_BASE_URL = PATH_PROXY
    ? serviceBase(8080, '/svc/gateway')
    : '/gateway';
export const AUTH_SERVICE_BASE_URL = PATH_PROXY
    ? serviceBase(5005, '/svc/auth')
    : '/auth';
// Internal DB service port (6000 — browsers block 6000 as X11). Dashboard
// reads go through the gateway (/api/* proxy) so the browser never hits a
// blocked or CORS-fragile direct DB port.
export const GENERAL_DB_SERVICE_BASE_URL = serviceBase(6000, '/svc/db');
export const GAME_ORCHESTRATOR_BASE_URL = API_BASE_URL;
/** Browser-safe base for dashboard reads (proxied by general_orchestrator). */
export const DASHBOARD_API_BASE_URL = GAME_ORCHESTRATOR_BASE_URL;
export const MONGO_BASE_URL = serviceBase(27017, '/svc/mongo');

export const BIOLOGICAL_SERVICE_BASE_URL = serviceBase(5001, '/svc/pattern');
/** Mobility service (proxies to the MAPF Docker app on 5002) */
export const MOBILITY_SERVICE_BASE_URL = serviceBase(5000, '/svc/mobility');
/** Direct MAPF (usually via mobility gateway; exposed for health/debug) */
export const MAPF_SERVICE_BASE_URL = serviceBase(5002, '/svc/mapf');
export const MARKOV_SERVICE_BASE_URL = serviceBase(5004, '/svc/markov');
/** Supply-chain / logistics resource management solver */
export const RESOURCE_MGMT_SERVICE_BASE_URL = serviceBase(5006, '/svc/resource');
/** Digital Twins mission planning (fleet + geo-fence graph solver) */
export const DIGITAL_TWINS_SERVICE_BASE_URL = serviceBase(5007, '/svc/digital-twins');
export const ART_SERVICE_BASE_URL = BIOLOGICAL_SERVICE_BASE_URL;

/**
 * Arcade GM + upload-orchestrator bases.
 * Dev GM ports match mission_control/launch_all.sh.
 * Uploads go through the gateway /{game_id}/upload_agent path (same as Snake /
 * Puzzle Master) so CORS, JWT, and on-demand orchestrator launch stay consistent.
 * Prod path-proxy: nginx /svc/gateway/ → :8080, /svc/<slug>/ → GM.
 */
function arcadePair(gmPort, slug, gameId) {
    return {
        API_BASE: serviceBase(gmPort, `/svc/${slug}`),
        UPLOAD_URL: `${GAME_ORCHESTRATOR_BASE_URL}/${gameId}/upload_agent`,
    };
}

export const ARCADE = {
    tic_tac_toe: arcadePair(5040, 'tic-tac-toe', 'tic_tac_toe'),
    connect_four: arcadePair(5070, 'connect-four', 'connect_four'),
    pong: arcadePair(5068, 'pong', 'pong'),
    one_armed_bandit: arcadePair(5074, 'bandit', 'one_armed_bandit'),
    blackjack: arcadePair(5076, 'blackjack', 'blackjack'),
    lights_out: arcadePair(5078, 'lights-out', 'lights_out'),
    battleship: arcadePair(5080, 'battleship', 'battleship'),
    numberlink: arcadePair(5082, 'numberlink', 'numberlink'),
    minesweeper: arcadePair(5084, 'minesweeper', 'minesweeper'),
    road_fighter: arcadePair(5086, 'road-fighter', 'road_fighter'),
    pac_man: arcadePair(5088, 'pac-man', 'pac_man'),
    two_joint_pole: arcadePair(5090, 'two-joint-pole', 'two_joint_pole'),
    tetris: arcadePair(5092, 'tetris', 'tetris'),
    space_invader: arcadePair(5094, 'space-invader', 'space_invader'),
    adventure_quest: arcadePair(5096, 'adventure-quest', 'adventure_quest'),
    santa_drop: arcadePair(5098, 'santa-drop', 'santa_drop'),
    sliding_tiles_puzzle: arcadePair(5003, 'tiles', 'sliding_tiles_puzzle'),
    snake2d: arcadePair(5020, 'snake', 'snake2d'),
};

export const API_ENDPOINTS = {
    LOGIN: `${AUTH_SERVICE_BASE_URL}/login`,
    REGISTER: `${AUTH_SERVICE_BASE_URL}/register`,
    GUEST_LOGIN: `${AUTH_SERVICE_BASE_URL}/guest-login`,
    VERIFY_EMAIL: `${AUTH_SERVICE_BASE_URL}/verify-email`,
    RESEND_VERIFICATION: `${AUTH_SERVICE_BASE_URL}/resend-verification`,
    FORGOT_PASSWORD: `${AUTH_SERVICE_BASE_URL}/forgot-password`,
    RESET_PASSWORD: `${AUTH_SERVICE_BASE_URL}/reset-password`,
    API_TOKENS: `${AUTH_SERVICE_BASE_URL}/api-tokens`,
    API_TOKEN: (tokenId) => `${AUTH_SERVICE_BASE_URL}/api-tokens/${tokenId}`,
    ME: `${AUTH_SERVICE_BASE_URL}/me`,

    TURING_PATTERNS: `${BIOLOGICAL_SERVICE_BASE_URL}/api`,
    LOTKA_VOLTERRA: `${BIOLOGICAL_SERVICE_BASE_URL}/api/lotka-volterra`,
    FISHER_EQUATION: `${BIOLOGICAL_SERVICE_BASE_URL}/api/fisher`,
    FITZHUGH_NAGUMO: `${BIOLOGICAL_SERVICE_BASE_URL}/api/fitzHugh-nagumo`,
    HODGKIN_HUXLEY: `${BIOLOGICAL_SERVICE_BASE_URL}/api/hodgkin-huxley`,
    SCHELLING_SEGREGATION: `${BIOLOGICAL_SERVICE_BASE_URL}/api/segregation`,
    EPIDEMIC_SIR: `${BIOLOGICAL_SERVICE_BASE_URL}/api/epidemic`,
    FLOCKING: `${BIOLOGICAL_SERVICE_BASE_URL}/api/flocking`,
    MOBILITY_SYSTEMS: `${MOBILITY_SERVICE_BASE_URL}/api`,
    MOBILITY_SOLVE_GRID: `${MOBILITY_SERVICE_BASE_URL}/solve_grid`,
    MARKOV_CHAINS: `${MARKOV_SERVICE_BASE_URL}/api`,
    RESOURCE_MGMT: `${RESOURCE_MGMT_SERVICE_BASE_URL}/api`,
    DIGITAL_TWINS: `${DIGITAL_TWINS_SERVICE_BASE_URL}/api/dt`,
    DIGITAL_TWINS_HEALTH: `${DIGITAL_TWINS_SERVICE_BASE_URL}/api/dt/health`,
    DIGITAL_TWINS_MISSION_PLAN: `${DIGITAL_TWINS_SERVICE_BASE_URL}/api/dt/mission/plan`,
    DIGITAL_TWINS_MISSION_RUN: `${DIGITAL_TWINS_SERVICE_BASE_URL}/api/dt/mission/run`,
    DIGITAL_TWINS_MISSION_PENDING: `${DIGITAL_TWINS_SERVICE_BASE_URL}/api/dt/mission/pending`,
    DIGITAL_TWINS_MISSION_QUEUE: `${DIGITAL_TWINS_SERVICE_BASE_URL}/api/dt/mission/queue`,
    DIGITAL_TWINS_MISSIONS_PAST: `${DIGITAL_TWINS_SERVICE_BASE_URL}/api/dt/missions/past`,
    DIGITAL_TWINS_TELEMETRY_HISTORY: `${DIGITAL_TWINS_SERVICE_BASE_URL}/api/dt/telemetry/history`,
    AUDIO_VISUALIZER: `${ART_SERVICE_BASE_URL}/api/audio-visualizer`,
    AUDIO_VISUALIZER_HEALTH: `${ART_SERVICE_BASE_URL}/api/health`,

    // --- Arcade Dashboard / DB (via gateway /api/* → general_db_services) ---
    // Do NOT call GENERAL_DB_SERVICE_BASE_URL from the browser (port 6000 is
    // blocked; 6000 can still fail depending on environment/CORS).
    USER_STATS: (userId) => `${DASHBOARD_API_BASE_URL}/api/users/${userId}/stats`,
    USER_SUBMISSIONS: (userId) => `${DASHBOARD_API_BASE_URL}/api/users/${userId}/submissions`,
    AGENT_DETAIL: (agentId) => `${DASHBOARD_API_BASE_URL}/api/agents/${agentId}`,
    AGENT_SUBMISSIONS: (agentId) => `${DASHBOARD_API_BASE_URL}/api/agents/${agentId}/submissions`,
    HIGHLIGHTS: `${DASHBOARD_API_BASE_URL}/api/highlights`,
    LEADERBOARD: (gameId, source) => {
        const url = `${DASHBOARD_API_BASE_URL}/api/leaderboard`;
        const params = new URLSearchParams();
        if (gameId) params.set('game', gameId);
        if (source) params.set('source', source);
        const qs = params.toString();
        return qs ? `${url}?${qs}` : url;
    },
    /** Co-op team scores (space_invader_coop | adventure_quest_coop). */
    COOP_LEADERBOARD: (gameId, limit = 10) =>
        `${DASHBOARD_API_BASE_URL}/api/coop/leaderboard?game=${encodeURIComponent(gameId)}&limit=${limit}`,
    /** Recent completed runs (arcade hub fallback when nothing is live). */
    RECENT_RUNS: ({ limit = 5, gameId, verified } = {}) => {
        const url = `${DASHBOARD_API_BASE_URL}/api/runs`;
        const params = new URLSearchParams();
        if (limit) params.set('limit', String(limit));
        if (gameId) params.set('game_id', gameId);
        if (verified != null) params.set('verified', verified ? '1' : '0');
        const qs = params.toString();
        return qs ? `${url}?${qs}` : url;
    },
    /** Persisted SDK / interactive sessions (status=active for in-progress). */
    SESSIONS: ({ status, limit, gameId, userId } = {}) => {
        const url = `${DASHBOARD_API_BASE_URL}/api/sessions`;
        const params = new URLSearchParams();
        if (status) params.set('status', status);
        if (limit) params.set('limit', String(limit));
        if (gameId) params.set('game_id', gameId);
        if (userId) params.set('user_id', userId);
        const qs = params.toString();
        return qs ? `${url}?${qs}` : url;
    },
    RUN_DETAIL: (runId) => `${DASHBOARD_API_BASE_URL}/api/runs/${runId}`,
    /** Admin-only site analytics (server-side gated on privilege_level=="admin"). */
    ADMIN_ANALYTICS: `${DASHBOARD_API_BASE_URL}/api/admin/analytics`,
    /** Public SPA pageview beacon (anonymous + authenticated; path + timestamp only). */
    PAGE_HIT: `${DASHBOARD_API_BASE_URL}/api/hits`,
    /**
     * Persistent "Last run" card source of truth (server-driven, not client
     * upload-response state). Logged-in users get their own last run for the
     * game; guests/anonymous get the latest run by any guest for that game.
     */
    LAST_RUN: (gameId, { userId, role } = {}) => {
        const url = `${DASHBOARD_API_BASE_URL}/api/runs/last`;
        const params = new URLSearchParams();
        if (gameId) params.set('game_id', gameId);
        if (userId) params.set('user_id', userId);
        if (role) params.set('role', role);
        const qs = params.toString();
        return qs ? `${url}?${qs}` : url;
    },
    /** Browser → gateway → DB: verified manual (and other) run inserts. */
    SUBMIT_RUN: `${DASHBOARD_API_BASE_URL}/api/runs`,
    /** Browser → gateway → DB: seed + single-use signed token for a ranked run (registered users). */
    SEED_TOKEN: `${DASHBOARD_API_BASE_URL}/api/seed_token`,
    GAMES_LIST: `${DASHBOARD_API_BASE_URL}/api/games`,

    // --- Snake / Sliding Tiles (gateway-routed, Mongo-registered games) ---
    // Leaderboard + run detail: same /api/* contract as Arcade dashboard.
    // Upload: gateway /{game_id}/upload_agent (on-demand upload orchestrator).
    // SDK play: /sdk/sessions* (server GM + client player) — see SDK_* below.
    // Stale paths like /{game}/run do not exist on upload orchestrators.
    SNAKE_LEADERBOARD: `${DASHBOARD_API_BASE_URL}/api/leaderboard?game=snake2d`,
    SNAKE_UPLOAD_AGENT: `${GAME_ORCHESTRATOR_BASE_URL}/snake2d/upload_agent`,
    /**
     * Thin-FE Snake GM base (session create / move / info).
     * Dev: CRA proxy `/snake` → :5020. Prod path-proxy: `/svc/snake`.
     */
    SNAKE_GM_BASE: PATH_PROXY ? serviceBase(5020, '/svc/snake') : '/snake',
    SLIDING_TILES_LEADERBOARD: `${DASHBOARD_API_BASE_URL}/api/leaderboard?game=sliding_tiles_puzzle`,
    SLIDING_TILES_UPLOAD_AGENT: `${GAME_ORCHESTRATOR_BASE_URL}/sliding_tiles_puzzle/upload_agent`,
    SLIDING_TILES_GM_BASE: PATH_PROXY ? serviceBase(5003, '/svc/tiles') : '/tiles',
    LIGHTS_GM_BASE: PATH_PROXY ? serviceBase(5078, '/svc/lights-out') : '/lights',
    TTT_GM_BASE: PATH_PROXY ? serviceBase(5040, '/svc/tic-tac-toe') : '/ttt',
    C4_GM_BASE: PATH_PROXY ? serviceBase(5070, '/svc/connect-four') : '/c4',
    MINES_GM_BASE: PATH_PROXY ? serviceBase(5084, '/svc/minesweeper') : '/mines',
    NUMBERLINK_GM_BASE: PATH_PROXY ? serviceBase(5082, '/svc/numberlink') : '/numberlink',
    BATTLESHIP_GM_BASE: PATH_PROXY ? serviceBase(5080, '/svc/battleship') : '/battleship',
    BLACKJACK_GM_BASE: PATH_PROXY ? serviceBase(5076, '/svc/blackjack') : '/blackjack',
    BANDIT_GM_BASE: PATH_PROXY ? serviceBase(5074, '/svc/bandit') : '/bandit',
    AQ_GM_BASE: PATH_PROXY ? serviceBase(5096, '/svc/adventure-quest') : '/aq',
    SANTA_GM_BASE: PATH_PROXY ? serviceBase(5098, '/svc/santa-drop') : '/santa',
    PONG_GM_BASE: PATH_PROXY ? serviceBase(5068, '/svc/pong') : '/pong',
    TETRIS_GM_BASE: PATH_PROXY ? serviceBase(5092, '/svc/tetris') : '/tetris',
    PACMAN_GM_BASE: PATH_PROXY ? serviceBase(5088, '/svc/pac-man') : '/pacman',
    ROAD_FIGHTER_GM_BASE: PATH_PROXY ? serviceBase(5086, '/svc/road-fighter') : '/road',
    SPACE_INVADER_GM_BASE: PATH_PROXY ? serviceBase(5094, '/svc/space-invader') : '/space',
    TJP_GM_BASE: PATH_PROXY ? serviceBase(5090, '/svc/two-joint-pole') : '/tjp',

    // --- SDK session provisioning (general_orchestrator) ---
    SDK_GAMES: `${GAME_ORCHESTRATOR_BASE_URL}/sdk/games`,
    SDK_SESSIONS: `${GAME_ORCHESTRATOR_BASE_URL}/sdk/sessions`,
    SDK_SESSION: (sessionId) => `${GAME_ORCHESTRATOR_BASE_URL}/sdk/sessions/${sessionId}`,
    SDK_SESSION_COMPLETE: (sessionId) =>
        `${GAME_ORCHESTRATOR_BASE_URL}/sdk/sessions/${sessionId}/complete`,
    /** Read-only live game state for spectating an in-progress SDK session. */
    SDK_SESSION_STATE: (sessionId) => `${GAME_ORCHESTRATOR_BASE_URL}/sdk/sessions/${sessionId}/state`,
};
